import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'config/axios';
import { MdOutlineQuiz, MdOutlineInsights, MdAccessTime } from 'react-icons/md';
import Helmet from 'components/Helmet';
import Button from 'components/Button';
import Loader from 'components/Loader';
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import { setAlert } from 'store/error';
import './index.css';

type StatisticalAnalysis = {
    satAvgScore: number;
    actAvgScore: number;
    satStdDev: number;
    actStdDev: number;
    tStatistic: number;
    pValue: number;
    isSignificant: boolean;
    satSampleSize: number;
    actSampleSize: number;
};

type TestResults = {
    studentTestId: string;
    satRWScore: number | null;
    satMathScore: number | null;
    actEnglishScore: number | null;
    actMathScore: number | null;
    satRWTime?: number;
    satMathTime?: number;
    actEnglishTime?: number;
    actMathTime?: number;
    satTotal: number;
    actTotal: number;
    recommendation: 'SAT' | 'ACT' | 'Either';
    completedAt: string | null;
    statisticalAnalysis?: StatisticalAnalysis | null;
};

type TestInfo = {
    testId: string;
    name: string;
    description?: string;
    hasCompletedTest: boolean;
    hasExistingAttempt: boolean;
    existingAttemptId: string | null;
    results?: TestResults;
};

function TestFinder() {
    const [isLoading, setIsLoading] = useState(true);
    const [testInfo, setTestInfo] = useState<TestInfo | null>(null);
    const [isStarting, setIsStarting] = useState(false);

    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { user } = useAppSelector(state => state.user);

    useEffect(() => {
        // Fetch test info
        axios.get('testfinder/')
            .then(res => {
                setTestInfo(res.data);
                setIsLoading(false);
            })
            .catch(error => {
                dispatch(setAlert(error));
                setIsLoading(false);
            });
    }, []);

    const handleStartTest = () => {
        if (testInfo?.hasExistingAttempt && testInfo?.existingAttemptId) {
            // Resume existing test
            navigate('/test-finder/test', { state: { freshStart: false } });
        } else {
            // Start new test
            setIsStarting(true);
            axios.post('testfinder/')
                .then(res => {
                    navigate('/test-finder/test', { state: { freshStart: true } });
                })
                .catch(error => {
                    dispatch(setAlert(error));
                    setIsStarting(false);
                });
        }
    };

    if (isLoading) return <Loader />;

    // Show results if test has been completed
    if (testInfo?.hasCompletedTest && testInfo?.results) {
        const { results } = testInfo;
        const completedDate = results.completedAt 
            ? new Date(results.completedAt).toLocaleDateString('en-US', { 
                month: 'short', 
                day: 'numeric', 
                year: 'numeric' 
            })
            : '';
        
        // Calculate correct counts from raw scores (7 questions per section)
        const satRWCorrect = results.satRWScore ?? 0;
        const satMathCorrect = results.satMathScore ?? 0;
        const actEnglishCorrect = results.actEnglishScore ?? 0;
        const actMathCorrect = results.actMathScore ?? 0;
        const satTotalCorrect = satRWCorrect + satMathCorrect;
        const actTotalCorrect = actEnglishCorrect + actMathCorrect;
        
        // Format time in minutes and seconds
        const formatTime = (seconds: number | undefined) => {
            if (!seconds) return '0:00';
            const mins = Math.floor(seconds / 60);
            const secs = Math.round(seconds % 60);
            return `${mins}:${secs.toString().padStart(2, '0')}`;
        };
        
        // Generate recommendation explanation
        const getRecommendationExplanation = () => {
            const stats = results.statisticalAnalysis;
            const lines: string[] = [];
            
            if (results.recommendation === 'SAT') {
                lines.push('Based on your reading/writing and math question scores, you seem more comfortable with SAT-style questions → Try our SAT prep solutions!');
            } else if (results.recommendation === 'ACT') {
                lines.push('Based on your reading/writing and math question scores, you seem more comfortable with ACT-style questions → Try our Online ACT test solution!');
            } else {
                lines.push('Based on your reading/writing and math question scores, you performed similarly on both tests.');
                lines.push("If you're comfortable with science, the ACT might be a good choice.");
            }
            
            return lines;
        };
        
        return (
            <div className='layout'>
                <Helmet
                    title='SAT or ACT? - Results - The SAT Crash Course'
                    description='Your personalized SAT vs ACT recommendation based on your diagnostic test performance.'
                />
                <div className='testFinderReport'>
                    {/* Header */}
                    <div className='testFinderReport__header'>
                        <div className='testFinderReport__userInfo'>
                            <p className='testFinderReport__userName'>{user?.firstName} {user?.lastName}</p>
                            <p className='testFinderReport__userEmail'>{user?.email}</p>
                            <p className='testFinderReport__date'>{completedDate}</p>
                        </div>
                        <h1 className='testFinderReport__title'>SAT vs ACT Report</h1>
                    </div>
                    
                    {/* Main Content */}
                    <div className='testFinderReport__content'>
                        {/* Left Sidebar */}
                        <div className='testFinderReport__sidebar'>
                            {/* Test Type Badge */}
                            <div className={`testFinderReport__badge testFinderReport__badge--${results.recommendation.toLowerCase()}`}>
                                <span className='testFinderReport__badgeLabel'>
                                    {results.recommendation === 'Either' ? 'EITHER' : results.recommendation}
                                </span>
                                <p className='testFinderReport__badgeText'>
                                    You are {results.recommendation === 'Either' ? 'suited for either' : `a${results.recommendation === 'ACT' ? 'n' : ''}`}
                                </p>
                                <p className='testFinderReport__badgeType'>
                                    {results.recommendation === 'Either' ? 'TEST TYPE' : `${results.recommendation} TYPE`}
                                </p>
                            </div>
                            
                            {/* Score Summary */}
                            <div className='testFinderReport__scoreSummary'>
                                <div className='testFinderReport__scoreBox'>
                                    <span className='testFinderReport__scoreLabel'>SAT Total Score</span>
                                    <div className='testFinderReport__scoreDisplay'>
                                        <span className='testFinderReport__scoreNum'>{satTotalCorrect}</span>
                                        <span className='testFinderReport__scoreMax'>/ 14</span>
                                        <span className='testFinderReport__scoreTag testFinderReport__scoreTag--sat'>SAT</span>
                                    </div>
                                    <div className='testFinderReport__scoreBar'>
                                        <div 
                                            className='testFinderReport__scoreBarFill testFinderReport__scoreBarFill--sat' 
                                            style={{ width: `${(satTotalCorrect / 14) * 100}%` }}
                                        />
                                    </div>
                                </div>
                                
                                <div className='testFinderReport__scoreBox'>
                                    <span className='testFinderReport__scoreLabel'>ACT Total Score</span>
                                    <div className='testFinderReport__scoreDisplay'>
                                        <span className='testFinderReport__scoreNum'>{actTotalCorrect}</span>
                                        <span className='testFinderReport__scoreMax'>/ 14</span>
                                        <span className='testFinderReport__scoreTag testFinderReport__scoreTag--act'>ACT</span>
                                    </div>
                                    <div className='testFinderReport__scoreBar'>
                                        <div 
                                            className='testFinderReport__scoreBarFill testFinderReport__scoreBarFill--act' 
                                            style={{ width: `${(actTotalCorrect / 14) * 100}%` }}
                                        />
                                    </div>
                                </div>
                            </div>
                            
                            {/* Review Test Button */}
                            <Button
                                className='testFinderReport__reviewButton'
                                onClick={() => navigate(`/review/test-finder/${results.studentTestId}`)}
                                secondary
                            >
                                Review Test
                            </Button>
                        </div>
                        
                        {/* Right Content */}
                        <div className='testFinderReport__main'>
                            {/* Raw Score Tables */}
                            <div className='testFinderReport__rawScores'>
                                <h2 className='testFinderReport__sectionTitle'>Raw Score</h2>
                                
                                <div className='testFinderReport__tables'>
                                    {/* Reading and Writing Table */}
                                    <div className='testFinderReport__table'>
                                        <h3 className='testFinderReport__tableTitle'>Reading and Writing</h3>
                                        <table>
                                            <thead>
                                                <tr>
                                                    <th></th>
                                                    <th>Correct</th>
                                                    <th>Time</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td>SAT</td>
                                                    <td>{satRWCorrect}/7</td>
                                                    <td>{formatTime(results.satRWTime)}</td>
                                                </tr>
                                                <tr>
                                                    <td>ACT</td>
                                                    <td>{actEnglishCorrect}/7</td>
                                                    <td>{formatTime(results.actEnglishTime)}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    
                                    {/* Math Table */}
                                    <div className='testFinderReport__table'>
                                        <h3 className='testFinderReport__tableTitle'>Math</h3>
                                        <table>
                                            <thead>
                                                <tr>
                                                    <th></th>
                                                    <th>Correct</th>
                                                    <th>Time</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td>SAT</td>
                                                    <td>{satMathCorrect}/7</td>
                                                    <td>{formatTime(results.satMathTime)}</td>
                                                </tr>
                                                <tr>
                                                    <td>ACT</td>
                                                    <td>{actMathCorrect}/7</td>
                                                    <td>{formatTime(results.actMathTime)}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Overview Section */}
                            <div className='testFinderReport__overview'>
                                <h2 className='testFinderReport__sectionTitle'>Overview</h2>
                                <div className='testFinderReport__overviewBox'>
                                    {getRecommendationExplanation().map((line, index) => (
                                        <p key={index} className='testFinderReport__overviewText'>{line}</p>
                                    ))}
                                </div>
                                
                                {/* CTA Buttons */}
                                <div className='testFinderReport__ctaButtons'>
                                    <button className='testFinderReport__ctaButton testFinderReport__ctaButton--act'
                                    onClick={() => navigate('/tests/digital-act')}>
                                        Try our Digital ACT test solution!
                                    </button>
                                    <button
                                        className='testFinderReport__ctaButton testFinderReport__ctaButton--sat'
                                        onClick={() => navigate('/courses')}
                                    >
                                        Try our SAT Prep solution!
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* Footer */}
                    <div className='testFinderReport__footer'>
                        <p>(856) 843 - 5227 / support@welledlabs.com / www.thesatcrashcourse.com</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className='layout'>
            <Helmet
                title='SAT or ACT? - The SAT Crash Course'
                description='Not sure whether SAT or ACT is right for you? Take our free diagnostic test to find out which test suits you better!'
            />
            <div className='testFinder'>
                <h1 className='testFinder__title'>
                    SAT or ACT?
                </h1>
                
                <div className='testFinder__card'>
                    <div className='testFinder__feature'>
                        <MdOutlineQuiz className='testFinder__featureIcon' />
                        <div className='testFinder__featureContent'>
                            <h3 className='testFinder__featureTitle'>Quick Diagnostic</h3>
                            <p className='testFinder__featureDesc'>
                                Answer 28 questions covering the SAT and ACT. Our diagnostic is designed to analyze your strengths and weaknesses across both tests, providing you with insights into which test aligns best with your skills and preferences.      
                            </p>
                        </div>
                    </div>
                    
                    <div className='testFinder__feature'>
                        <MdOutlineInsights className='testFinder__featureIcon' />
                        <div className='testFinder__featureContent'>
                            <h3 className='testFinder__featureTitle'>Personalized Results</h3>
                            <p className='testFinder__featureDesc'>
                                You will receive a personalized recommendation at the end: SAT, ACT, or Either—based on your performance. 
                            </p>
                        </div>
                    </div>
                    
                    <div className='testFinder__feature'>
                        <MdAccessTime className='testFinder__featureIcon' />
                        <div className='testFinder__featureContent'>
                            <h3 className='testFinder__featureTitle'>Timing</h3>
                            <p className='testFinder__featureDesc'>
                                Each section is timed at 10 minutes. Total estimated time: ~40 minutes. We strongly recommend taking the diagnostic in a quiet environment without distractions to get the most accurate recommendation.
                            </p>
                        </div>
                    </div>
                </div>

                {testInfo?.hasExistingAttempt && (
                    <p className='testFinder__resumeNotice'>
                        You have an incomplete attempt. Click below to resume.
                    </p>
                )}

                <Button
                    className='testFinder__button'
                    onClick={handleStartTest}
                    disabled={isStarting}
                >
                    {isStarting 
                        ? 'STARTING...' 
                        : testInfo?.hasExistingAttempt 
                            ? 'Resume Test' 
                            : 'Start Diagnostic'
                    }
                </Button>
            </div>
        </div>
    );
}

export default TestFinder;
